import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { API_HOST_WMS } from "config/api.config";
import { api } from "network/network";
import { APIendpoints, DefaultParams } from "utils/constants/APIendpoints";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL, ENTITY_TYPE_SELLER_OUTLET } from "utils/constants/others";
import { makeEncodedQueryParams } from "operations/operations";
import { SHIPPING_NOTE_API_STATUSES } from "./planShipmentShippingNotesUtils";

const getCurrentDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeTotalCount = (totalCount, scheduleGroups) => {
  const parsedTotalCount = Number(totalCount);
  return Number.isFinite(parsedTotalCount)
    ? parsedTotalCount
    : scheduleGroups.length;
};

const getErrorMessage = (error, fallbackMessage) => {
  // Try to extract actual error from internal_error field
  const internalError = error?.response?.data?.error?.internal_error;
  if (internalError && typeof internalError === "string") {
    // Extract the actual error message from pattern: "OrignalError : <message>, ErrorType : ..."
    const match = internalError.match(/OrignalError\s*:\s*(.+?),\s*ErrorType/);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  return (
    error?.response?.data?.error?.message ||
    error?.message ||
    fallbackMessage ||
    "Something went wrong. Please try again."
  );
};

const fetchShippingNotesPage = async (baseQuery, pageNumber, pageSize, statuses) => {
  const statusesToFetch = Array.isArray(statuses) && statuses.length > 0
    ? statuses
    : SHIPPING_NOTE_API_STATUSES;

  const statusResults = await Promise.all(
    statusesToFetch.map(async (status) => {
      const query = { ...baseQuery, status, page_size: pageSize, page_number: pageNumber };
      const response = await api.get(API_HOST_WMS, APIendpoints.getAdvanceShippingNotes, { query });
      const responseData = response?.data?.response || {};
      const pageItems = Array.isArray(responseData.asn_infos) ? responseData.asn_infos : [];
      return pageItems.map((n) => ({ ...n, status: n?.status || status }));
    })
  );
  return statusResults.flat();
};

const enrichShippingNotes = async (shippingNotes, baseQuery, pageSize) => {
  const shippingNotesByKey = new Map();
  shippingNotes.forEach((n) => {
    const asnId = Number(n?.asn_id);
    const key = asnId > 0 ? String(asnId) : JSON.stringify(n);
    if (!shippingNotesByKey.has(key)) {
      shippingNotesByKey.set(key, n);
    }
  });
  const dedupedNotes = Array.from(shippingNotesByKey.values());

  const scheduleGroupIdsToEnrich = Array.from(
    new Set(
      dedupedNotes
        .filter((n) => !Array.isArray(n.invoice_numbers) || !Array.isArray(n.purchase_order_numbers))
        .map((n) => Number(n.schedule_group_id))
        .filter((id) => Number.isInteger(id) && id > 0)
    )
  );

  const scheduleGroupDetails = await Promise.all(
    scheduleGroupIdsToEnrich.map(async (scheduleGroupId) => {
      try {
        const detailQuery = {
          ...DefaultParams.getPOScheduleGroupDetails,
          vendor_id: baseQuery.entity_id,
          schedule_group_id: scheduleGroupId,
          page_size: pageSize,
          page_number: 1,
        };
        const response = await api.get(API_HOST_WMS, APIendpoints.getPOScheduleGroupDetails, { query: detailQuery });
        const responseData = response?.data?.response || {};
        return [scheduleGroupId, responseData.schedule_group || null];
      } catch (_) {
        return [scheduleGroupId, null];
      }
    })
  );

  const scheduleGroupsById = new Map(scheduleGroupDetails);
  return dedupedNotes.map((n) => {
    if (Array.isArray(n.invoice_numbers) && Array.isArray(n.purchase_order_numbers)) return n;
    const sg = scheduleGroupsById.get(Number(n.schedule_group_id));
    const pos = Array.isArray(sg?.purchase_orders) ? sg.purchase_orders : [];
    const matchingPOs = pos.filter(
      (po) => Array.isArray(po?.invoices) && po.invoices.some((inv) => Number(inv?.asn_id) === Number(n.asn_id))
    );
    return {
      ...n,
      invoice_numbers: matchingPOs.flatMap((po) =>
        po.invoices
          .filter((inv) => Number(inv?.asn_id) === Number(n.asn_id))
          .map((inv) => inv.invoice_number)
          .filter(Boolean)
      ),
      purchase_order_numbers: matchingPOs.map((po) => po.purchase_order_number).filter(Boolean),
    };
  });
};

const mapPlanShipmentPurchaseOrderSearchItem = (po = {}) => ({
  id: Number(po.purchase_order_id) || 0,
  purchase_order_id: Number(po.purchase_order_id) || 0,
  purchase_order_number: po.purchase_order_number || "",
  warehouse_code: po.warehouse_code || "",
  derived_status: po.derived_status || po.purchase_order_status || "",
  purchase_order_status: po.purchase_order_status || "",
  expected_delivery_date: po.expected_delivery_date,
  po_amount: Number(po.po_amount) || 0,
  created_by_phone_number: po.created_by_phone_number || "",
  total_skus: Number(po.total_sku_count) || 0,
  total_sku_count: Number(po.total_sku_count) || 0,
  total_weight: Number(po.total_weight_in_kg) || 0,
  total_weight_in_kg: Number(po.total_weight_in_kg) || 0,
  remaining_weight_to_schedule: Number(po.remaining_weight_to_schedule) || 0,
  zone_name: po.zone_name || "",
  zone: po.zone_name || "",
});

const isNotFoundError = (error) =>
  error?.response?.status === 404 ||
  /not found/i.test(error?.response?.data?.error?.message || "");

const normalizePurchaseOrders = (rawPOs) =>
  rawPOs.map((po) => {
    const items = Array.isArray(po.purchase_order_items) ? po.purchase_order_items : [];
    const totalWeight = items.reduce(
      (sum, item) =>
        sum +
        (Number(item.quantity_ordered) || 0) * (Number(item.weight_per_packet) || 0),
      0
    );
    return { ...po, total_skus: items.length, total_weight: totalWeight };
  });

const usePlanShipmentStore = create(
  devtools((set, get) => ({
    getPOScheduleGroupsStatus: "",
    poScheduleGroups: [],
    poScheduleGroupsTotalCount: 0,
    poScheduleGroupsHasMore: false,
    poScheduleGroupsNextPage: 1,
    poScheduleGroupsQueryParams: null,
    poScheduleGroupsError: "",
    getPOScheduleGroupDetailsStatus: "",
    poScheduleGroupDetails: null,
    poScheduleGroupDetailsError: "",
    updatePOScheduleInvoicesStatus: "",
    updatePOScheduleInvoicesError: "",
    shippingNoteSelection: null,
    createAdvanceShippingNoteStatus: "",
    createAdvanceShippingNoteResponse: null,
    createAdvanceShippingNoteError: "",
    updateAdvanceShippingNoteStatus: "",
    updateAdvanceShippingNoteError: "",
    deleteAdvanceShippingNoteStatus: "",
    deleteAdvanceShippingNoteError: "",
    getAdvanceShippingNotesStatus: "",
    advanceShippingNotes: [],
    advanceShippingNotesHasMore: false,
    advanceShippingNotesNextPage: 1,
    advanceShippingNotesQueryParams: null,
    advanceShippingNotesError: "",
    getAdvanceShippingNoteDetailsStatus: "",
    advanceShippingNoteDetails: null,
    advanceShippingNoteDetailsError: "",
    getPurchaseOrdersStatus: "",
    purchaseOrders: [],
    purchaseOrdersTotalCount: 0,
    purchaseOrdersHasMore: false,
    purchaseOrdersNextPage: 1,
    purchaseOrdersQueryParams: null,
    purchaseOrdersError: "",
    getPOScheduleSlotsStatus: "",
    poScheduleSlotDates: [],
    poScheduleSlotsNoAvailable: false,
    poScheduleSlotsExceedWeight: false,
    getPOScheduleSlotsError: "",
    createPOScheduleGroupStatus: "",
    createdScheduleGroupId: null,
    createdScheduleDetails: null,
    createPOScheduleGroupError: "",
    getPlanShipmentPurchaseOrdersStatus: "",
    planShipmentPurchaseOrders: [],
    planShipmentPurchaseOrdersHasMore: false,
    planShipmentPurchaseOrdersNextPage: 1,
    planShipmentPurchaseOrdersQueryParams: null,
    getPlanShipmentPurchaseOrdersError: "",
    getScheduleGroupPurchaseOrdersStatus: "",
    scheduleGroupPurchaseOrders: [],
    getScheduleGroupPurchaseOrdersError: "",
    updatePOScheduleGroupStatus: "",
    updatedScheduleGroupId: null,
    updatedScheduleDetails: null,
    updatePOScheduleGroupError: "",
    getPlanShipmentHelpSectionsStatus: "",
    planShipmentHelpSections: [],
    planShipmentHelpSectionsError: "",
    planShipmentAsnConfig: null,

    getPOScheduleGroups: async (params) => {
      const baseQuery = {
        ...DefaultParams.getPOScheduleGroups,
        ...params,
      };
      const pageSize = Math.max(Number(baseQuery.page_size) || 20, 1);

      set({
        getPOScheduleGroupsStatus: STATUS_BEGIN,
        poScheduleGroups: [],
        poScheduleGroupsTotalCount: 0,
        poScheduleGroupsHasMore: false,
        poScheduleGroupsNextPage: 2,
        poScheduleGroupsQueryParams: { ...baseQuery, page_size: pageSize },
        poScheduleGroupsError: "",
      });

      try {
        const query = { ...baseQuery, page_size: pageSize, page_number: 1 };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPOScheduleGroups,
          { query }
        );
        const responseData = response?.data?.response || {};

        const pageItems = Array.isArray(responseData.schedule_groups)
          ? responseData.schedule_groups
          : [];

        set({
          getPOScheduleGroupsStatus: STATUS_DONE,
          poScheduleGroups: pageItems,
          poScheduleGroupsTotalCount: pageItems.length,
          poScheduleGroupsHasMore: pageItems.length === pageSize,
          poScheduleGroupsError: "",
        });
      } catch (error) {
        set({
          getPOScheduleGroupsStatus: STATUS_FAIL,
          poScheduleGroups: [],
          poScheduleGroupsTotalCount: 0,
          poScheduleGroupsHasMore: false,
          poScheduleGroupsError: getErrorMessage(
            error,
            "Unable to load scheduled shipments. Please try again."
          ),
        });
      }
    },

    loadMorePOScheduleGroups: async () => {
      const state = get();
      if (
        !state.poScheduleGroupsHasMore ||
        state.getPOScheduleGroupsStatus === STATUS_BEGIN
      )
        return;

      set({ getPOScheduleGroupsStatus: STATUS_BEGIN });

      const baseQuery = state.poScheduleGroupsQueryParams || DefaultParams.getPOScheduleGroups;
      const pageSize = Number(baseQuery.page_size) || 20;
      const pageNumber = state.poScheduleGroupsNextPage;

      try {
        const query = { ...baseQuery, page_size: pageSize, page_number: pageNumber };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPOScheduleGroups,
          { query }
        );
        const responseData = response?.data?.response || {};

        const pageItems = Array.isArray(responseData.schedule_groups)
          ? responseData.schedule_groups
          : [];

        set((prev) => ({
          getPOScheduleGroupsStatus: STATUS_DONE,
          poScheduleGroups: [...prev.poScheduleGroups, ...pageItems],
          poScheduleGroupsTotalCount: prev.poScheduleGroupsTotalCount + pageItems.length,
          poScheduleGroupsHasMore: pageItems.length === pageSize,
          poScheduleGroupsNextPage: pageNumber + 1,
          poScheduleGroupsError: "",
        }));
      } catch (error) {
        set({
          getPOScheduleGroupsStatus: STATUS_FAIL,
          poScheduleGroupsHasMore: false,
          poScheduleGroupsError: getErrorMessage(
            error,
            "Unable to load more scheduled shipments. Please try again."
          ),
        });
      }
    },

    getPOScheduleGroupDetails: async (params) => {
      set({
        getPOScheduleGroupDetailsStatus: STATUS_BEGIN,
        poScheduleGroupDetails: null,
        poScheduleGroupDetailsError: "",
      });

      const query = {
        ...DefaultParams.getPOScheduleGroupDetails,
        ...params,
      };

      try {
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPOScheduleGroupDetails,
          { query }
        );
        const responseData = response?.data?.response || {};

        set({
          getPOScheduleGroupDetailsStatus: STATUS_DONE,
          poScheduleGroupDetails: responseData.schedule_group || null,
          poScheduleGroupDetailsError: "",
        });
      } catch (error) {
        set({
          getPOScheduleGroupDetailsStatus: STATUS_FAIL,
          poScheduleGroupDetails: null,
          poScheduleGroupDetailsError: getErrorMessage(
            error,
            "Unable to load this PO schedule group. Please try again."
          ),
        });
      }
    },

    getAdvanceShippingNotes: async (params) => {
      const baseQuery = {
        ...DefaultParams.getAdvanceShippingNotes,
        ...params,
      };
      const pageSize = Math.max(Number(baseQuery.page_size) || 20, 1);

      const statuses = Array.isArray(baseQuery.statuses) && baseQuery.statuses.length > 0
        ? baseQuery.statuses
        : SHIPPING_NOTE_API_STATUSES;

      set({
        getAdvanceShippingNotesStatus: STATUS_BEGIN,
        advanceShippingNotes: [],
        advanceShippingNotesHasMore: false,
        advanceShippingNotesNextPage: 2,
        advanceShippingNotesQueryParams: { ...baseQuery, page_size: pageSize, statuses },
        advanceShippingNotesError: "",
      });

      try {
        const pageItems = await fetchShippingNotesPage(baseQuery, 1, pageSize, statuses);
        const enriched = await enrichShippingNotes(pageItems, baseQuery, pageSize);

        set({
          getAdvanceShippingNotesStatus: STATUS_DONE,
          advanceShippingNotes: enriched,
          advanceShippingNotesHasMore: pageItems.length >= pageSize,
          advanceShippingNotesError: "",
        });
      } catch (error) {
        set({
          getAdvanceShippingNotesStatus: STATUS_FAIL,
          advanceShippingNotes: [],
          advanceShippingNotesHasMore: false,
          advanceShippingNotesError: getErrorMessage(
            error,
            "Unable to load shipping notes. Please try again."
          ),
        });
      }
    },

    loadMoreAdvanceShippingNotes: async () => {
      const state = get();
      if (
        !state.advanceShippingNotesHasMore ||
        state.getAdvanceShippingNotesStatus === STATUS_BEGIN
      )
        return;

      set({ getAdvanceShippingNotesStatus: STATUS_BEGIN });

      const baseQuery = state.advanceShippingNotesQueryParams || {};
      const pageSize = Number(baseQuery.page_size) || 20;
      const pageNumber = state.advanceShippingNotesNextPage;
      const statuses = baseQuery.statuses;

      try {
        const pageItems = await fetchShippingNotesPage(baseQuery, pageNumber, pageSize, statuses);
        const enriched = await enrichShippingNotes(pageItems, baseQuery, pageSize);

        const existingIds = new Set(
          state.advanceShippingNotes.map((n) => {
            const id = Number(n?.asn_id);
            return id > 0 ? String(id) : JSON.stringify(n);
          })
        );
        const newItems = enriched.filter((n) => {
          const id = Number(n?.asn_id);
          const key = id > 0 ? String(id) : JSON.stringify(n);
          return !existingIds.has(key);
        });

        set((prev) => ({
          getAdvanceShippingNotesStatus: STATUS_DONE,
          advanceShippingNotes: [...prev.advanceShippingNotes, ...newItems],
          advanceShippingNotesHasMore: pageItems.length >= pageSize,
          advanceShippingNotesNextPage: pageNumber + 1,
          advanceShippingNotesError: "",
        }));
      } catch (error) {
        set({
          getAdvanceShippingNotesStatus: STATUS_FAIL,
          advanceShippingNotesHasMore: false,
          advanceShippingNotesError: getErrorMessage(
            error,
            "Unable to load more shipping notes. Please try again."
          ),
        });
      }
    },

    getAdvanceShippingNoteDetails: async (params) => {
      set({
        getAdvanceShippingNoteDetailsStatus: STATUS_BEGIN,
        advanceShippingNoteDetails: null,
        advanceShippingNoteDetailsError: "",
      });

      const query = {
        ...DefaultParams.getAdvanceShippingNoteDetails,
        ...params,
      };

      try {
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getAdvanceShippingNoteDetails,
          { query }
        );
        const responseData = response?.data?.response || {};

        const asnInfo = responseData.asn_info || null;
        if (!asnInfo || !(Number(asnInfo.asn_id) > 0)) {
          throw new Error("The shipping-note detail response is empty");
        }

        set({
          getAdvanceShippingNoteDetailsStatus: STATUS_DONE,
          advanceShippingNoteDetails: asnInfo,
          advanceShippingNoteDetailsError: "",
        });
      } catch (error) {
        set({
          getAdvanceShippingNoteDetailsStatus: STATUS_FAIL,
          advanceShippingNoteDetails: null,
          advanceShippingNoteDetailsError: getErrorMessage(
            error,
            "Unable to load this shipping note. Please try again."
          ),
        });
      }
    },

    uploadPlanShipmentInvoiceDocument: async (file) => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("fileType", "vendor_invoice");
      formData.append("source", "SELLER_HUB");
      formData.append("detectPDFBlur", "false");

      const response = await api.post(
        API_HOST_WMS,
        APIendpoints.uploadDocument,
        formData
      );
      const invoicePath = response?.data?.response?.response;
      const isValid = response?.data?.response?.is_valid;

      if (isValid === "false") {
        throw new Error(
          response?.data?.response?.validation_message ||
            "The invoice document is unclear. Upload a clearer copy."
        );
      }

      if (!invoicePath || typeof invoicePath !== "string") {
        throw new Error("Invoice upload did not return a document path");
      }

      return invoicePath;
    },

    updatePOScheduleInvoices: async (requestBody) => {
      set({
        updatePOScheduleInvoicesStatus: STATUS_BEGIN,
        updatePOScheduleInvoicesError: "",
      });

      try {
        await api.put(
          API_HOST_WMS,
          APIendpoints.updatePOScheduleInvoices,
          requestBody
        );

        set({
          updatePOScheduleInvoicesStatus: STATUS_DONE,
          updatePOScheduleInvoicesError: "",
        });
      } catch (error) {
        const message = getErrorMessage(
          error,
          "Unable to update invoices. Please try again."
        );
        set({
          updatePOScheduleInvoicesStatus: STATUS_FAIL,
          updatePOScheduleInvoicesError: message,
        });
        throw error;
      }
    },

    resetUpdatePOScheduleInvoices: () => {
      set({
        updatePOScheduleInvoicesStatus: "",
        updatePOScheduleInvoicesError: "",
      });
    },

    setShippingNoteSelection: (selection) => {
      const normalizeInvoiceIds = (invoiceIds) =>
        Array.from(
          new Set(
            (invoiceIds || [])
              .map(Number)
              .filter(
                (invoiceId) => Number.isInteger(invoiceId) && invoiceId > 0
              )
          )
        );

      const scheduleGroupId = Number(selection?.schedule_group_id);
      const asnId = Number(selection?.asn_id) || 0;
      const vendorInvoiceIds = normalizeInvoiceIds(
        selection?.vendor_invoice_ids
      );
      const originalInvoiceIds = normalizeInvoiceIds(
        selection?.original_invoice_ids
      );
      const isValidSelection =
        scheduleGroupId > 0 && (vendorInvoiceIds.length > 0 || asnId > 0);

      set({
        shippingNoteSelection: isValidSelection
          ? {
              schedule_group_id: scheduleGroupId,
              vendor_invoice_ids: vendorInvoiceIds,
              asn_id: asnId,
              original_invoice_ids: originalInvoiceIds,
            }
          : null,
      });
    },

    clearShippingNoteSelection: () => {
      set({ shippingNoteSelection: null });
    },

    uploadPlanShipmentASNDocument: async (file) => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("fileType", "asn_document");
      formData.append("source", "SELLER_HUB");
      formData.append("detectPDFBlur", "false");

      const response = await api.post(
        API_HOST_WMS,
        APIendpoints.uploadDocument,
        formData
      );
      const documentPath = response?.data?.response?.response;

      if (!documentPath || typeof documentPath !== "string") {
        throw new Error("Document upload did not return a document path");
      }

      return documentPath;
    },

    createAdvanceShippingNote: async (requestBody) => {
      set({
        createAdvanceShippingNoteStatus: STATUS_BEGIN,
        createAdvanceShippingNoteResponse: null,
        createAdvanceShippingNoteError: "",
      });

      try {
        const response = await api.post(
          API_HOST_WMS,
          APIendpoints.createAdvanceShippingNote,
          requestBody
        );
        const responseData = response?.data?.response || {};

        const asnInfo = responseData.asn_info || null;
        if (!asnInfo || !(Number(asnInfo.asn_id) > 0)) {
          throw new Error("Shipping note creation did not return an ASN ID");
        }
        set({
          createAdvanceShippingNoteStatus: STATUS_DONE,
          createAdvanceShippingNoteResponse: asnInfo,
          createAdvanceShippingNoteError: "",
        });
        return asnInfo;
      } catch (error) {
        const message = getErrorMessage(
          error,
          "Unable to create the shipping note. Please try again."
        );
        set({
          createAdvanceShippingNoteStatus: STATUS_FAIL,
          createAdvanceShippingNoteResponse: null,
          createAdvanceShippingNoteError: message,
        });
        throw error;
      }
    },

    resetCreateAdvanceShippingNote: () => {
      set({
        createAdvanceShippingNoteStatus: "",
        createAdvanceShippingNoteResponse: null,
        createAdvanceShippingNoteError: "",
      });
    },

    updateAdvanceShippingNote: async (requestBody) => {
      set({
        updateAdvanceShippingNoteStatus: STATUS_BEGIN,
        updateAdvanceShippingNoteError: "",
      });

      try {
        await api.put(
          API_HOST_WMS,
          APIendpoints.updateAdvanceShippingNote,
          requestBody
        );

        set({
          updateAdvanceShippingNoteStatus: STATUS_DONE,
          updateAdvanceShippingNoteError: "",
        });
      } catch (error) {
        const message = getErrorMessage(
          error,
          "Unable to update the shipping note. Please try again."
        );
        set({
          updateAdvanceShippingNoteStatus: STATUS_FAIL,
          updateAdvanceShippingNoteError: message,
        });
        throw error;
      }
    },

    resetUpdateAdvanceShippingNote: () => {
      set({
        updateAdvanceShippingNoteStatus: "",
        updateAdvanceShippingNoteError: "",
      });
    },

    deleteAdvanceShippingNote: async (requestBody) => {
      set({
        deleteAdvanceShippingNoteStatus: STATUS_BEGIN,
        deleteAdvanceShippingNoteError: "",
      });

      try {
        await api.delete(
          API_HOST_WMS,
          APIendpoints.deleteAdvanceShippingNote,
          requestBody
        );

        set({
          deleteAdvanceShippingNoteStatus: STATUS_DONE,
          deleteAdvanceShippingNoteError: "",
        });
      } catch (error) {
        const message = getErrorMessage(
          error,
          "Unable to delete the shipping note. Please try again."
        );
        set({
          deleteAdvanceShippingNoteStatus: STATUS_FAIL,
          deleteAdvanceShippingNoteError: message,
        });
        throw error;
      }
    },

    resetDeleteAdvanceShippingNote: () => {
      set({
        deleteAdvanceShippingNoteStatus: "",
        deleteAdvanceShippingNoteError: "",
      });
    },

    getPOScheduleSlots: async (params) => {
      set({
        getPOScheduleSlotsStatus: STATUS_BEGIN,
        poScheduleSlotDates: [],
        poScheduleSlotsNoAvailable: false,
        poScheduleSlotsExceedWeight: false,
        getPOScheduleSlotsError: "",
      });

      const requiredCapacity = Number(params?.required_capacity) || 0;
      const asnConfig = get().planShipmentAsnConfig;
      const maxWeightLimit =
        Number(asnConfig?.slot_full_schedule_creation_max_limit_in_kg) > 0
          ? Number(asnConfig.slot_full_schedule_creation_max_limit_in_kg)
          : 5000;

      try {
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPOScheduleSlots,
          { query: params }
        );
        const responseData = response?.data?.response || {};

        // Group flat delivery_slots by delivery_date
        const grouped = {};
        (responseData.delivery_slots || []).forEach((slot) => {
          if (Number(slot.slot_id) === 0) return;

          // Normalise delivery_date — API may send a full ISO timestamp
          const rawDate = slot.delivery_date;
          const dateKey =
            rawDate && typeof rawDate === "string"
              ? rawDate.slice(0, 10)
              : rawDate;

          if (!grouped[dateKey]) {
            grouped[dateKey] = { date: dateKey, is_allotted: false, slots: [] };
          }
          grouped[dateKey].slots.push({
            slot_id: slot.slot_id,
            slot_key: `${dateKey}::${slot.slot_id}`,
            display_name: slot.delivery_slot_time_text,
            available_capacity: slot.available_capacity,
            start_mins: slot.start_slot_time,
            end_mins: slot.end_slot_time,
            zone: slot.zone || "",
            schedule_group: slot.schedule_group || null,
          });
        });
        const slotDates = Object.values(grouped)
          .filter((d) => d.slots.length > 0)
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        const exceedsWeightLimit = requiredCapacity >= maxWeightLimit;
        const noSlotsAvailable =
          !exceedsWeightLimit &&
          (slotDates.length === 0 ||
            slotDates.every((d) => {
              const visibleSlots = d.slots.filter((s) => Number(s.available_capacity) !== -1);
              return visibleSlots.length === 0 || visibleSlots.every((s) => Number(s.available_capacity) === 0);
            }));

        set({
          getPOScheduleSlotsStatus: STATUS_DONE,
          poScheduleSlotDates: slotDates,
          poScheduleSlotsNoAvailable: noSlotsAvailable,
          poScheduleSlotsExceedWeight: exceedsWeightLimit,
          getPOScheduleSlotsError: "",
        });
      } catch (error) {
        set({
          getPOScheduleSlotsStatus: STATUS_FAIL,
          poScheduleSlotDates: [],
          poScheduleSlotsNoAvailable: false,
          poScheduleSlotsExceedWeight: false,
          getPOScheduleSlotsError: getErrorMessage(
            error,
            "Unable to load available slots. Please try again."
          ),
        });
      }
    },

    createPOScheduleGroup: async (requestBody, scheduleDetails) => {
      set({
        createPOScheduleGroupStatus: STATUS_BEGIN,
        createdScheduleGroupId: null,
        createdScheduleDetails: null,
        createPOScheduleGroupError: "",
      });

      try {
        const response = await api.post(
          API_HOST_WMS,
          APIendpoints.createPOScheduleGroup,
          requestBody
        );
        const responseData = response?.data?.response || {};

        const scheduleGroupId = Number(responseData.schedule_group_id);
        if (!scheduleGroupId) {
          throw new Error("Schedule group creation did not return a group ID");
        }

        set({
          createPOScheduleGroupStatus: STATUS_DONE,
          createdScheduleGroupId: scheduleGroupId,
          createdScheduleDetails: scheduleDetails
            ? {
                ...scheduleDetails,
                schedule_group_number: responseData.schedule_group_number || null,
                schedule_group_id: scheduleGroupId,
              }
            : null,
          createPOScheduleGroupError: "",
        });
      } catch (error) {
        set({
          createPOScheduleGroupStatus: STATUS_FAIL,
          createdScheduleGroupId: null,
          createdScheduleDetails: null,
          createPOScheduleGroupError: getErrorMessage(
            error,
            "Unable to schedule POs. Please try again."
          ),
        });
      }
    },

    resetCreatePOScheduleGroup: () => {
      set({
        createPOScheduleGroupStatus: "",
        createdScheduleGroupId: null,
        createdScheduleDetails: null,
        createPOScheduleGroupError: "",
      });
    },

    getPlanShipmentPurchaseOrders: async (params) => {
      const baseQuery = {
        ...DefaultParams.getPlanShipmentPurchaseOrders,
        ...params,
      };
      const pageSize = Math.max(Number(baseQuery.page_size) || 50, 1);

      set({
        getPlanShipmentPurchaseOrdersStatus: STATUS_BEGIN,
        planShipmentPurchaseOrders: [],
        planShipmentPurchaseOrdersHasMore: false,
        planShipmentPurchaseOrdersNextPage: 2,
        planShipmentPurchaseOrdersQueryParams: { ...baseQuery, page_size: pageSize },
        getPlanShipmentPurchaseOrdersError: "",
      });

      try {
        const query = { ...baseQuery, page_number: 1, page_size: pageSize };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPlanShipmentPurchaseOrders,
          { query }
        );
        const responseData = response?.data?.response || {};

        const pageItems = Array.isArray(responseData.purchase_orders)
          ? responseData.purchase_orders
          : [];

        set({
          getPlanShipmentPurchaseOrdersStatus: STATUS_DONE,
          planShipmentPurchaseOrders: pageItems,
          planShipmentPurchaseOrdersHasMore: pageItems.length === pageSize,
          getPlanShipmentPurchaseOrdersError: "",
        });
      } catch (error) {
        set({
          getPlanShipmentPurchaseOrdersStatus: STATUS_FAIL,
          planShipmentPurchaseOrders: [],
          planShipmentPurchaseOrdersHasMore: false,
          getPlanShipmentPurchaseOrdersError: getErrorMessage(
            error,
            "Unable to load purchase orders. Please try again."
          ),
        });
      }
    },

    loadMorePlanShipmentPurchaseOrders: async () => {
      const state = get();
      if (
        !state.planShipmentPurchaseOrdersHasMore ||
        state.getPlanShipmentPurchaseOrdersStatus === STATUS_BEGIN
      )
        return;

      set({ getPlanShipmentPurchaseOrdersStatus: STATUS_BEGIN });

      const baseQuery = state.planShipmentPurchaseOrdersQueryParams || {};
      const pageSize = Number(baseQuery.page_size) || 50;
      const pageNumber = state.planShipmentPurchaseOrdersNextPage;

      try {
        const query = { ...baseQuery, page_number: pageNumber, page_size: pageSize };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPlanShipmentPurchaseOrders,
          { query }
        );
        const responseData = response?.data?.response || {};

        const pageItems = Array.isArray(responseData.purchase_orders)
          ? responseData.purchase_orders
          : [];

        set((prev) => ({
          getPlanShipmentPurchaseOrdersStatus: STATUS_DONE,
          planShipmentPurchaseOrders: [...prev.planShipmentPurchaseOrders, ...pageItems],
          planShipmentPurchaseOrdersHasMore: pageItems.length === pageSize,
          planShipmentPurchaseOrdersNextPage: pageNumber + 1,
          getPlanShipmentPurchaseOrdersError: "",
        }));
      } catch (error) {
        set({
          getPlanShipmentPurchaseOrdersStatus: STATUS_FAIL,
          planShipmentPurchaseOrdersHasMore: false,
          getPlanShipmentPurchaseOrdersError: getErrorMessage(
            error,
            "Unable to load more purchase orders. Please try again."
          ),
        });
      }
    },

    getScheduleGroupPurchaseOrders: async (params) => {
      set({
        getScheduleGroupPurchaseOrdersStatus: STATUS_BEGIN,
        scheduleGroupPurchaseOrders: [],
        getScheduleGroupPurchaseOrdersError: "",
      });

      const query = {
        ...DefaultParams.getScheduleGroupPurchaseOrders,
        ...params,
      };

      try {
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getScheduleGroupPurchaseOrders,
          { query }
        );
        const responseData = response?.data?.response || {};

        set({
          getScheduleGroupPurchaseOrdersStatus: STATUS_DONE,
          scheduleGroupPurchaseOrders: Array.isArray(responseData.purchase_orders)
            ? responseData.purchase_orders
            : [],
          getScheduleGroupPurchaseOrdersError: "",
        });
      } catch (error) {
        set({
          getScheduleGroupPurchaseOrdersStatus: STATUS_FAIL,
          scheduleGroupPurchaseOrders: [],
          getScheduleGroupPurchaseOrdersError: getErrorMessage(
            error,
            "Unable to load schedule purchase orders. Please try again."
          ),
        });
      }
    },

    updatePOScheduleGroup: async (requestBody, scheduleDetails) => {
      set({
        updatePOScheduleGroupStatus: STATUS_BEGIN,
        updatedScheduleGroupId: null,
        updatedScheduleDetails: null,
        updatePOScheduleGroupError: "",
      });

      try {
        const response = await api.put(
          API_HOST_WMS,
          APIendpoints.updatePOScheduleGroup,
          requestBody
        );
        const responseData = response?.data?.response || {};

        const scheduleGroupId = Number(responseData.schedule_group_id);
        if (!scheduleGroupId) {
          throw new Error("Schedule group update did not return a group ID");
        }

        set({
          updatePOScheduleGroupStatus: STATUS_DONE,
          updatedScheduleGroupId: scheduleGroupId,
          updatedScheduleDetails: scheduleDetails
            ? {
                ...scheduleDetails,
                schedule_group_number: responseData.schedule_group_number || null,
                schedule_group_id: scheduleGroupId,
              }
            : null,
          updatePOScheduleGroupError: "",
        });
      } catch (error) {
        set({
          updatePOScheduleGroupStatus: STATUS_FAIL,
          updatedScheduleGroupId: null,
          updatedScheduleDetails: null,
          updatePOScheduleGroupError: getErrorMessage(
            error,
            "Unable to update schedule. Please try again."
          ),
        });
      }
    },

    resetUpdatePOScheduleGroup: () => {
      set({
        updatePOScheduleGroupStatus: "",
        updatedScheduleGroupId: null,
        updatedScheduleDetails: null,
        updatePOScheduleGroupError: "",
      });
    },

    getPurchaseOrders: async (params) => {
      const baseQuery = {
        vendor_id: "",
        page_number: 1,
        page_size: 50,
        derived_statuses: ["UNSCHEDULED", "PARTIALLY_SCHEDULED"],
        start_expected_delivery_date: getCurrentDateString(),
        ...params,
      };
      const pageSize = Math.max(Number(baseQuery.page_size) || 50, 1);

      set({
        getPurchaseOrdersStatus: STATUS_BEGIN,
        purchaseOrders: [],
        purchaseOrdersTotalCount: 0,
        purchaseOrdersHasMore: false,
        purchaseOrdersNextPage: 2,
        purchaseOrdersQueryParams: { ...baseQuery, page_size: pageSize },
        purchaseOrdersError: "",
      });

      try {
        const query = { ...baseQuery, page_number: 1, page_size: pageSize };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPlanShipmentPurchaseOrders,
          { query }
        );
        const responseData = response?.data?.response || {};

        const pageItems = (
          Array.isArray(responseData.purchase_orders) ? responseData.purchase_orders : []
        ).map(mapPlanShipmentPurchaseOrderSearchItem);

        set({
          getPurchaseOrdersStatus: STATUS_DONE,
          purchaseOrders: pageItems,
          purchaseOrdersTotalCount: pageItems.length,
          purchaseOrdersHasMore: pageItems.length === pageSize,
          purchaseOrdersError: "",
        });
      } catch (error) {
        set({
          getPurchaseOrdersStatus: STATUS_FAIL,
          purchaseOrders: [],
          purchaseOrdersTotalCount: 0,
          purchaseOrdersHasMore: false,
          purchaseOrdersError: getErrorMessage(
            error,
            "Unable to load unscheduled purchase orders. Please try again."
          ),
        });
      }
    },

    loadMorePurchaseOrders: async () => {
      const state = get();
      if (
        !state.purchaseOrdersHasMore ||
        state.getPurchaseOrdersStatus === STATUS_BEGIN
      )
        return;

      set({ getPurchaseOrdersStatus: STATUS_BEGIN });

      const baseQuery = state.purchaseOrdersQueryParams || {};
      const pageSize = Number(baseQuery.page_size) || 50;
      const pageNumber = state.purchaseOrdersNextPage;

      try {
        const query = { ...baseQuery, page_number: pageNumber, page_size: pageSize };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.getPlanShipmentPurchaseOrders,
          { query }
        );
        const responseData = response?.data?.response || {};

        const pageItems = (
          Array.isArray(responseData.purchase_orders) ? responseData.purchase_orders : []
        ).map(mapPlanShipmentPurchaseOrderSearchItem);

        set((prev) => ({
          getPurchaseOrdersStatus: STATUS_DONE,
          purchaseOrders: [...prev.purchaseOrders, ...pageItems],
          purchaseOrdersTotalCount: prev.purchaseOrdersTotalCount + pageItems.length,
          purchaseOrdersHasMore: pageItems.length === pageSize,
          purchaseOrdersNextPage: pageNumber + 1,
          purchaseOrdersError: "",
        }));
      } catch (error) {
        set({
          getPurchaseOrdersStatus: STATUS_FAIL,
          purchaseOrdersHasMore: false,
          purchaseOrdersError: getErrorMessage(
            error,
            "Unable to load more purchase orders. Please try again."
          ),
        });
      }
    },

    searchPurchaseOrders: async ({ vendor_id, purchase_order_id } = {}) => {
      const parsedId = Number(purchase_order_id);

      set({
        getPurchaseOrdersStatus: STATUS_BEGIN,
        purchaseOrders: [],
        purchaseOrdersTotalCount: 0,
        purchaseOrdersHasMore: false,
        purchaseOrdersQueryParams: null,
        purchaseOrdersError: "",
      });

      if (!(Number.isInteger(parsedId) && parsedId > 0)) {
        set({ getPurchaseOrdersStatus: STATUS_DONE, purchaseOrders: [], purchaseOrdersTotalCount: 0 });
        return;
      }

      try {
        const query = {
          vendor_id,
          purchase_order_id: parsedId,
          start_expected_delivery_date: getCurrentDateString(),
        };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.searchPlanShipmentPurchaseOrder,
          { query }
        );
        const responseData = response?.data?.response || {};
        const items = Array.isArray(responseData.purchase_orders)
          ? responseData.purchase_orders
          : [];
        const mapped = items.map(mapPlanShipmentPurchaseOrderSearchItem);

        set({
          getPurchaseOrdersStatus: STATUS_DONE,
          purchaseOrders: mapped,
          purchaseOrdersTotalCount: mapped.length,
          purchaseOrdersHasMore: false,
          purchaseOrdersError: "",
        });
      } catch (error) {
        if (isNotFoundError(error)) {
          set({
            getPurchaseOrdersStatus: STATUS_DONE,
            purchaseOrders: [],
            purchaseOrdersTotalCount: 0,
            purchaseOrdersHasMore: false,
            purchaseOrdersError: "",
          });
          return;
        }
        set({
          getPurchaseOrdersStatus: STATUS_FAIL,
          purchaseOrders: [],
          purchaseOrdersTotalCount: 0,
          purchaseOrdersHasMore: false,
          purchaseOrdersError: getErrorMessage(
            error,
            "Unable to search purchase orders. Please try again."
          ),
        });
      }
    },

    searchPOScheduleGroups: async ({ vendor_id, schedule_group_id } = {}) => {
      const parsedId = Number(schedule_group_id);

      set({
        getPOScheduleGroupsStatus: STATUS_BEGIN,
        poScheduleGroups: [],
        poScheduleGroupsTotalCount: 0,
        poScheduleGroupsHasMore: false,
        poScheduleGroupsQueryParams: null,
        poScheduleGroupsError: "",
      });

      if (!(Number.isInteger(parsedId) && parsedId > 0)) {
        set({ getPOScheduleGroupsStatus: STATUS_DONE, poScheduleGroups: [], poScheduleGroupsTotalCount: 0 });
        return;
      }

      try {
        const query = { vendor_id, schedule_group_id: parsedId };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.searchPOScheduleGroup,
          { query }
        );
        const responseData = response?.data?.response || {};
        const items = Array.isArray(responseData.schedule_groups)
          ? responseData.schedule_groups
          : [];

        set({
          getPOScheduleGroupsStatus: STATUS_DONE,
          poScheduleGroups: items,
          poScheduleGroupsTotalCount: items.length,
          poScheduleGroupsHasMore: false,
          poScheduleGroupsError: "",
        });
      } catch (error) {
        if (isNotFoundError(error)) {
          set({
            getPOScheduleGroupsStatus: STATUS_DONE,
            poScheduleGroups: [],
            poScheduleGroupsTotalCount: 0,
            poScheduleGroupsHasMore: false,
            poScheduleGroupsError: "",
          });
          return;
        }
        set({
          getPOScheduleGroupsStatus: STATUS_FAIL,
          poScheduleGroups: [],
          poScheduleGroupsTotalCount: 0,
          poScheduleGroupsHasMore: false,
          poScheduleGroupsError: getErrorMessage(
            error,
            "Unable to search schedules. Please try again."
          ),
        });
      }
    },

    searchAdvanceShippingNotes: async ({ entity_type, entity_id, asn_id } = {}) => {
      const parsedId = Number(asn_id);

      set({
        getAdvanceShippingNotesStatus: STATUS_BEGIN,
        advanceShippingNotes: [],
        advanceShippingNotesHasMore: false,
        advanceShippingNotesQueryParams: null,
        advanceShippingNotesError: "",
      });

      if (!(Number.isInteger(parsedId) && parsedId > 0)) {
        set({ getAdvanceShippingNotesStatus: STATUS_DONE, advanceShippingNotes: [] });
        return;
      }

      try {
        const query = {
          entity_type: entity_type || ENTITY_TYPE_SELLER_OUTLET,
          entity_id,
          asn_id: parsedId,
        };
        const response = await api.get(
          API_HOST_WMS,
          APIendpoints.searchAdvanceShippingNote,
          { query }
        );
        const responseData = response?.data?.response || {};
        const items = Array.isArray(responseData.asn_infos)
          ? responseData.asn_infos
          : [];

        set({
          getAdvanceShippingNotesStatus: STATUS_DONE,
          advanceShippingNotes: items,
          advanceShippingNotesHasMore: false,
          advanceShippingNotesError: "",
        });
      } catch (error) {
        if (isNotFoundError(error)) {
          set({
            getAdvanceShippingNotesStatus: STATUS_DONE,
            advanceShippingNotes: [],
            advanceShippingNotesHasMore: false,
            advanceShippingNotesError: "",
          });
          return;
        }
        set({
          getAdvanceShippingNotesStatus: STATUS_FAIL,
          advanceShippingNotes: [],
          advanceShippingNotesHasMore: false,
          advanceShippingNotesError: getErrorMessage(
            error,
            "Unable to search shipping notes. Please try again."
          ),
        });
      }
    },

    getPlanShipmentHelpSections: async (params) => {
      set({ getPlanShipmentHelpSectionsStatus: STATUS_BEGIN });
      let queryString = makeEncodedQueryParams(
        DefaultParams.getPlanShipmentHelpSectionsParams,
        params
      );
      const url = `${APIendpoints.getPlanShipmentHelpSections}${queryString}`;

      try {
        const response = await api.get(API_HOST_WMS, url);
        const responseData = response?.data?.response || {};
        set({
          planShipmentHelpSections: Array.isArray(responseData.help_section)
            ? responseData.help_section
            : [],
          planShipmentAsnConfig:
            responseData.asn_config &&
            typeof responseData.asn_config === "object"
              ? responseData.asn_config
              : null,
          getPlanShipmentHelpSectionsStatus: STATUS_DONE,
          planShipmentHelpSectionsError: "",
        });
      } catch (error) {
        set({
          planShipmentHelpSections: [],
          planShipmentAsnConfig: null,
          getPlanShipmentHelpSectionsStatus: STATUS_FAIL,
          planShipmentHelpSectionsError: getErrorMessage(
            error,
            "Unable to load help sections. Please try again."
          ),
        });
      }
    },
  }))
);

export default usePlanShipmentStore;
